import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalOpportunity, ExternalSearchResult } from '../../types/opportunity.types';
import { ExternalOpportunityCard } from './ExternalOpportunityCard';

interface SearchResultsProps {
  data?: ExternalSearchResult;
  isLoading: boolean;
  isError: boolean;
  onPageChange: (page: number) => void;
  onExclude?: (opportunity: ExternalOpportunity) => void;
}

function CardSkeleton() {
  return (
    <div className="bg-surface rounded-lg border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-3/4" />
      <div className="flex gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-28" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-6 w-32" />
      </div>
      <div className="flex gap-2 pt-2 border-t">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-8 w-28 ml-auto" />
      </div>
    </div>
  );
}

export function SearchResults({
  data,
  isLoading,
  isError,
  onPageChange,
  onExclude,
}: SearchResultsProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">
          Erreur lors de la recherche. Veuillez réessayer.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Utilisez les filtres ci-dessus pour rechercher des appels d'offres.</p>
      </div>
    );
  }

  if (data.opportunities.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Aucun résultat trouvé pour votre recherche.</p>
        <p className="text-sm mt-1">Essayez de modifier vos critères de recherche.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header avec compteur */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{data.total}</span>{' '}
          résultat{data.total > 1 ? 's' : ''} trouvé{data.total > 1 ? 's' : ''}
        </p>
      </div>

      {/* Grille de résultats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.opportunities.map((opportunity) => (
          <ExternalOpportunityCard
            key={opportunity.externalId}
            opportunity={opportunity}
            onExclude={onExclude}
          />
        ))}
      </div>

      {/* Pagination */}
      {data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(data.page - 1)}
            disabled={data.page <= 1}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Précédent
          </Button>
          <span className="text-sm text-muted-foreground px-4">
            Page {data.page} sur {data.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(data.page + 1)}
            disabled={data.page >= data.totalPages}
          >
            Suivant
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
